/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React, {
  ChangeEvent,
  useEffect,
  useMemo,
  useState
} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {MessageEndpoint, MessageEndpointTypes} from '@ion/core/src/types/entities';
import FormItem from '@ion/dls/src/components/forms/FormItem';
import FormSecureTextField from '@ion/dls/src/components/forms/FormSecureTextField';
import FormTextField from '@ion/dls/src/components/forms/FormTextField';
import {FormState} from '@ion/dls/src/hooks/useFormState';
import {
  getInitialStreamFormState,
  getIsStreamType,
  getMessageName,
  MessageEndpointFormFields
} from './MessageEndpointUtils';

export interface StreamMessageEndpointProps {
  messageEndpoint?: MessageEndpoint;
  formState: FormState;
  setFormState: Function;
  type: MessageEndpointTypes;
  isView?: boolean;
  isAdd?: boolean;
  isEdit?: boolean;
  isLoggedinUserAccount?: boolean;
  onFieldChange: (e: ChangeEvent) => void;
}

/**
 * Displays a Kinesis or SQS style account message endpoint form & handles all related form functionality.
 *
 * This is strictly to be used in the context of a @see MessageEndpoint component.
 *
 * ```typescript
 *
 * import StreamMessageEndpoint from './StreamMessageEndpoint';
 *
 * <StreamMessageEndpoint isView={isView}
 *   isAdd={isAdd}
 *   isLoggedinUserAccount={isLoggedinUserAccount}
 *   formState={formState}
 *   setFormState={setFormState}
 *   type={messageType}
 *   messageEndpoint={messageEndpoint}
 *   onFieldChange={onFieldChange} />
 *
 * ```
 */
export const StreamMessageEndpoint = ({
  type,
  isView,
  isAdd,
  isEdit,
  isLoggedinUserAccount,
  formState,
  setFormState,
  messageEndpoint,
  onFieldChange
}: StreamMessageEndpointProps) => {
  const [originalAccessKey, setOriginalAccessKey] = useState();
  const messageName                               = getMessageName(type);

  useEffect(function(): void {
    if (getIsStreamType(type)) {
      const newFormState = getInitialStreamFormState(type, messageEndpoint?.configuration);
      if (messageEndpoint?.configuration) {
        setOriginalAccessKey(messageEndpoint.configuration?.[MessageEndpointFormFields.AWS_ACCESS_KEY]);
      }
      setFormState(newFormState);
    } else {
      setFormState(null, true);
    }
  }, [messageEndpoint, type]);

  const streamType = translate(
    `admin-groups:dialog.messageEndpoint.fields.${MessageEndpointFormFields[messageName]}`,
    {returnObjects: true});

  const {
    accessKey,
    secretAccessKey,
    region
  } = translate('admin-groups:dialog.messageEndpoint.fields', {returnObjects: true});

  return (
    <>
      <FormItem>
        <FormTextField required
          disabled={isView || isLoggedinUserAccount}
          fieldLabel={streamType.label}
          placeholder={streamType.placeholder}
          errorText={streamType.errorMessage}
          hasError={formState[MessageEndpointFormFields[messageName]]?.error}
          value={formState[MessageEndpointFormFields[messageName]]?.value}
          name={MessageEndpointFormFields[messageName]}
          onChange={onFieldChange} />
      </FormItem>
      <FormItem>
        <FormSecureTextField required={isAdd || isView}
          disabled={isView || isLoggedinUserAccount}
          fallbackValue={originalAccessKey ?? ''}
          hideCharacters={!isEdit}
          showToggleButton={!isView}
          fieldLabel={accessKey.label}
          placeholder={accessKey.placeholder}
          errorText={accessKey.errorMessage}
          hasError={formState[MessageEndpointFormFields.AWS_ACCESS_KEY]?.error}
          value={formState[MessageEndpointFormFields.AWS_ACCESS_KEY]?.value}
          name={MessageEndpointFormFields.AWS_ACCESS_KEY}
          onChange={onFieldChange} />
      </FormItem>
      {!isView && (
        <FormItem>
          <FormSecureTextField required={isAdd || isView}
            fallbackValue={!isAdd ? undefined : ''}
            showToggleButton={!isView}
            fieldLabel={secretAccessKey.label}
            placeholder={secretAccessKey.placeholder}
            errorText={secretAccessKey.errorMessage}
            hasError={formState[MessageEndpointFormFields.AWS_SECRET_ACCESS_KEY]?.error}
            value={formState[MessageEndpointFormFields.AWS_SECRET_ACCESS_KEY]?.value}
            name={MessageEndpointFormFields.AWS_SECRET_ACCESS_KEY}
            onChange={onFieldChange}
            disabled={isView || isLoggedinUserAccount} />
        </FormItem>
      )}
      <FormItem>
        <FormTextField required
          fieldLabel={region.label}
          placeholder={region.placeholder}
          errorText={region.errorMessage}
          hasError={formState[MessageEndpointFormFields.REGION]?.error}
          value={formState[MessageEndpointFormFields.REGION]?.value}
          name={MessageEndpointFormFields.REGION}
          onChange={onFieldChange}
          disabled={isView || isLoggedinUserAccount} />
      </FormItem>
    </>
  );
};

export default StreamMessageEndpoint;
